import { type ReactNode, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Props {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '5xl' | '7xl' | 'wide'
  /** 배경(백드롭) 클릭으로도 닫을지 여부. 기본 false — X 버튼만 닫음. */
  closeOnBackdrop?: boolean
}

const SIZES = {
  sm:   'max-w-sm',
  md:   'max-w-md',
  lg:   'max-w-lg',
  xl:   'max-w-2xl',
  '2xl':'max-w-3xl',
  '5xl':'max-w-5xl',
  '7xl':'max-w-7xl',
  wide: 'max-w-[96vw]',   // 열 많은 표(출고 비고1/2 등)용 — 화면 거의 가득
}

// 열려 있는 Modal 을 스택으로 관리. Esc 는 최상단(가장 나중에 열린) 것만 닫음.
// (중첩 Modal 시 Esc 한 번에 여러 개 닫히는 것 방지)
// 각 인스턴스는 stable useRef 객체를 ID 로 사용 — 부모 리렌더에 흔들리지 않음.
const openStack: Array<object> = []

export function Modal({
  open, onClose, title, children, size = 'md',
  closeOnBackdrop = false,
}: Props) {
  // Modal 인스턴스 stable ID.
  const idRef = useRef<object>({})
  // 최신 onClose 참조 — 매 렌더마다 갱신하지만 useEffect 는 재실행하지 않음.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    const myId = idRef.current
    openStack.push(myId)
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // 최상단 Modal 만 이 이벤트에 반응.
      if (openStack[openStack.length - 1] !== myId) return
      onCloseRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      const idx = openStack.lastIndexOf(myId)
      if (idx >= 0) openStack.splice(idx, 1)
    }
  }, [open])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 px-4"
      onClick={closeOnBackdrop ? onClose : undefined}
    >
      <div
        className={cn(
          'bg-white rounded-2xl shadow-2xl w-full p-6 max-h-[95vh] overflow-y-auto relative',
          SIZES[size]
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* X 닫기 버튼 (우상단) */}
        <button
          type="button"
          onClick={onClose}
          aria-label="닫기"
          className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
        >
          <X className="w-4 h-4"/>
        </button>
        {title && <h3 className="text-lg font-bold mb-4 pr-8">{title}</h3>}
        {children}
      </div>
    </div>,
    document.body,
  )
}
